import { round } from 'lodash-es';
import type { FxValues, Offer } from '@/models/simulation/subtypes';

const FRACTION_DIGITS = 2 as const;

/** Everything the `amount-to-repay` row shows, in PLN and in the loan currency - the offers table, the printout and the offer sorting read it from here. */
export const useSimulationAmountToRepay = () => {
  /** `amount-to-repay` row: the backend total sum - the costs sum plus the amount paid out. */
  const getAmountToRepay = (offer: Offer): number => offer.totals.totalSum;
  const fxAmountToRepayCallback = (fxValues: FxValues) => fxValues.totalSum;

  /** The amount paid out, not the gross `capital` - credited costs are already in the costs sum. */
  const getAmountPaidOut = (offer: Offer): number => offer.totals.totalSum - offer.totals.costsSum;
  const fxAmountPaidOutCallback = (fxValues: FxValues) => fxValues.totalSum - fxValues.costsSum;

  const getInterestAmount = (offer: Offer): number => offer.totals.interest;
  const fxInterestCallback = (fxValues: FxValues) => fxValues.interest;

  /** Non-interest costs: the backend costs sum minus interest. */
  const getAdditionalCostsAmount = (offer: Offer): number => offer.totals.costsSum - getInterestAmount(offer);
  const fxAdditionalCostsCallback = (fxValues: FxValues) => fxValues.costsSum - fxInterestCallback(fxValues);

  /** Total amount to repay as a percentage of the amount paid out (e.g. 207%). */
  const getRepayToAmountPaidOutRatio = (offer: Offer): Nullable<number> => {
    const amountPaidOut: number = getAmountPaidOut(offer);
    return amountPaidOut > 0
      ? round((getAmountToRepay(offer) / amountPaidOut) * 100, FRACTION_DIGITS)
      : null;
  };

  return {
    getAmountToRepay,
    fxAmountToRepayCallback,
    getAmountPaidOut,
    fxAmountPaidOutCallback,
    getInterestAmount,
    fxInterestCallback,
    getAdditionalCostsAmount,
    fxAdditionalCostsCallback,
    getRepayToAmountPaidOutRatio,
  };
};
